import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import {
  calculatePos, createPosPlan, createPosShare, deletePosPlan, duplicatePosPlan, duplicatePosShare,
  fetchPosStructures, fetchPosTowers, listPosPlans, readPosPlan, readPosShare,
  revokePosShare, updatePosPlan, validatePosImport, importPosDocument,
} from '../composables/usePosApi';
import { boundedInteger, calculateRemainingPosResources, clampCoordinate, normalizeSecurityStatus, POS_MAX_STRUCTURES, radialPlacement } from '../pos/posPlanUtils';
import type {
  PosCalculation, PosCatalogStructure, PosEnvironment, PosInventoryKind, PosInventoryLine,
  PosPlanSnapshot, PosServerPlan, PosStructure, PosStructureState, PosTower,
} from '../../shared/pos';

const DRAFT_KEY = 'evefit.pos.draft.v1';
const STRUCTURE_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const INVENTORY_KINDS = new Set<PosInventoryKind>(['ammunition', 'fuel', 'charter', 'strontium', 'spare', 'other']);

function localId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function newDraft(): PosPlanSnapshot {
  return {
    name: 'Untitled POS plan', notes: '', towerTypeID: null, tower: null,
    environment: 'highsec', securityStatus: null, structures: [], inventory: [],
    fuel: { horizonDays: 7, fuelStock: null, strontiumStock: null },
  };
}

function asPositiveInteger(value: unknown): number | null {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
}

function normalizeInventory(value: unknown): PosInventoryLine[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 500).flatMap((entry): PosInventoryLine[] => {
    if (!entry || typeof entry !== 'object') return [];
    const item = entry as Partial<PosInventoryLine>;
    const typeID = asPositiveInteger(item.typeID);
    const quantity = asPositiveInteger(item.quantity);
    const kind = typeof item.kind === 'string' && INVENTORY_KINDS.has(item.kind as PosInventoryKind) ? item.kind as PosInventoryKind : null;
    if (!typeID || !quantity || !kind) return [];
    return [{ typeID, quantity: Math.min(quantity, 100_000_000), kind, label: typeof item.label === 'string' ? item.label.slice(0, 160) : undefined }];
  });
}

function normalizeSnapshot(candidate: unknown): PosPlanSnapshot | null {
  if (!candidate || typeof candidate !== 'object') return null;
  const source = candidate as Partial<PosPlanSnapshot>;
  const environment: PosEnvironment = ['highsec', 'lowsec', 'nullsec', 'wormhole'].includes(String(source.environment))
    ? source.environment as PosEnvironment : 'highsec';
  const seenIds = new Set<string>();
  const structures = (Array.isArray(source.structures) ? source.structures : []).slice(0, POS_MAX_STRUCTURES).flatMap((entry, index): PosStructure[] => {
    if (!entry || typeof entry !== 'object') return [];
    const structure = entry as Partial<PosStructure>;
    const typeID = asPositiveInteger(structure.typeID);
    if (!typeID) return [];
    const providedId = typeof structure.id === 'string' && STRUCTURE_ID.test(structure.id) ? structure.id : localId(`import-${index}`);
    const id = seenIds.has(providedId) ? localId(`import-${index}`) : providedId;
    seenIds.add(id);
    return [{
      id,
      typeID,
      state: structure.state === 'online' ? 'online' : 'offline',
      x: clampCoordinate(structure.x), y: clampCoordinate(structure.y), z: clampCoordinate(structure.z),
      chargeTypeID: asPositiveInteger(structure.chargeTypeID) ?? null,
      loadedQuantity: boundedInteger(structure.loadedQuantity ?? 0, 0, 1_000_000, 0),
      label: typeof structure.label === 'string' ? structure.label.slice(0, 160) : undefined,
      deploymentPhase: typeof structure.deploymentPhase === 'string' ? structure.deploymentPhase.slice(0, 80) : undefined,
      sortOrder: boundedInteger(structure.sortOrder ?? index, 0, 1_000_000, index),
      typeName: typeof structure.typeName === 'string' ? structure.typeName.slice(0, 160) : `Type ${typeID}`,
      role: structure.role || 'other', lifecycle: structure.lifecycle || 'unknown', effectConfidence: structure.effectConfidence || 'unsupported',
      footprint: typeof structure.footprint === 'number' && Number.isFinite(structure.footprint) ? structure.footprint : undefined,
      cpu: typeof structure.cpu === 'number' && Number.isFinite(structure.cpu) ? structure.cpu : undefined,
      powerGrid: typeof structure.powerGrid === 'number' && Number.isFinite(structure.powerGrid) ? structure.powerGrid : undefined,
      primaryEffect: typeof structure.primaryEffect === 'string' ? structure.primaryEffect.slice(0, 500) : undefined,
      placement: structure.placement,
    }];
  });
  return {
    id: typeof source.id === 'number' ? source.id : undefined,
    revision: typeof source.revision === 'number' ? source.revision : undefined,
    shareId: typeof source.shareId === 'string' ? source.shareId : null,
    name: String(source.name || 'Untitled POS plan').slice(0, 160),
    notes: String(source.notes || '').slice(0, 10_000),
    towerTypeID: asPositiveInteger(source.towerTypeID),
    tower: source.tower ?? null,
    environment,
    securityStatus: normalizeSecurityStatus(source.securityStatus),
    structures,
    inventory: normalizeInventory(source.inventory),
    fuel: {
      horizonDays: boundedInteger(source.fuel?.horizonDays ?? 7, 0, 365, 7),
      fuelStock: source.fuel?.fuelStock == null ? null : boundedInteger(source.fuel.fuelStock, 0, 1_000_000_000, 0),
      strontiumStock: source.fuel?.strontiumStock == null ? null : boundedInteger(source.fuel.strontiumStock, 0, 1_000_000_000, 0),
    },
    rulesVersion: typeof source.rulesVersion === 'string' ? source.rulesVersion : undefined,
    defaultDamageProfile: source.defaultDamageProfile,
  };
}

/** Sends only persisted input fields. Catalog enrichment and client totals never become API input. */
function planDocument(source: PosPlanSnapshot): PosPlanSnapshot {
  return {
    id: source.id,
    revision: source.revision,
    name: source.name,
    notes: source.notes,
    towerTypeID: source.towerTypeID,
    environment: source.environment,
    securityStatus: normalizeSecurityStatus(source.securityStatus),
    structures: source.structures.map((structure, index) => ({
      id: structure.id, typeID: structure.typeID, state: structure.state,
      x: clampCoordinate(structure.x), y: clampCoordinate(structure.y), z: clampCoordinate(structure.z),
      chargeTypeID: structure.chargeTypeID ?? null,
      loadedQuantity: boundedInteger(structure.loadedQuantity ?? 0, 0, 1_000_000, 0),
      label: structure.label?.slice(0, 160), deploymentPhase: structure.deploymentPhase?.slice(0, 80),
      sortOrder: boundedInteger(structure.sortOrder ?? index, 0, 1_000_000, index),
    })),
    inventory: normalizeInventory(source.inventory),
    fuel: {
      horizonDays: boundedInteger(source.fuel.horizonDays, 0, 365, 7),
      fuelStock: source.fuel.fuelStock == null ? null : boundedInteger(source.fuel.fuelStock, 0, 1_000_000_000, 0),
      strontiumStock: source.fuel.strontiumStock == null ? null : boundedInteger(source.fuel.strontiumStock, 0, 1_000_000_000, 0),
    },
    defaultDamageProfile: source.defaultDamageProfile,
    rulesVersion: source.rulesVersion,
  };
}

function draftFingerprint(plan: PosPlanSnapshot): string {
  const document = planDocument(plan);
  delete document.id;
  delete document.revision;
  return JSON.stringify(document);
}

function resourceFingerprint(plan: PosPlanSnapshot): string {
  return JSON.stringify({
    towerTypeID: plan.towerTypeID,
    structures: plan.structures
      .filter(structure => structure.state === 'online')
      .map(structure => [structure.id, structure.typeID, structure.state]),
  });
}

export const usePosStore = defineStore('pos', () => {
  const plan = ref<PosPlanSnapshot>(newDraft());
  const towers = ref<PosTower[]>([]);
  const catalog = ref<PosCatalogStructure[]>([]);
  const calculation = ref<PosCalculation | null>(null);
  const selectedIds = ref<string[]>([]);
  const comparisonTower = ref<PosTower | null>(null);
  const ownedPlans = ref<PosServerPlan[]>([]);
  const readOnly = ref(false);
  const loading = ref(false);
  const catalogLoading = ref(false);
  const error = ref<string | null>(null);
  const lastSavedAt = ref<string | null>(null);
  const catalogFilters = ref({ search: '', role: '', lifecycle: '', includeUnsupported: false });
  const loadedShareToken = ref<string | null>(null);
  let calculationTimer: number | undefined;
  let calculationSequence = 0;
  const calculatedResourceFingerprint = ref<string | null>(null);

  const selectedStructures = computed(() => plan.value.structures.filter(structure => selectedIds.value.includes(structure.id)));
  const selectedStructure = computed(() => selectedStructures.value.length === 1 ? selectedStructures.value[0] : null);
  const isSaved = computed(() => typeof plan.value.id === 'number');
  const shareUrl = computed(() => plan.value.shareId ? `${window.location.origin}/pos/${plan.value.shareId}` : null);
  const resourceHeadroom = computed(() => {
    const fingerprint = resourceFingerprint(plan.value);
    if (calculation.value && calculatedResourceFingerprint.value === fingerprint) {
      return {
        cpu: calculation.value.resources.cpu.available,
        powerGrid: calculation.value.resources.powerGrid.available,
      };
    }
    return calculateRemainingPosResources(plan.value.tower, plan.value.structures);
  });
  const towerComparison = computed(() => {
    const selected = plan.value.tower;
    const candidate = comparisonTower.value;
    if (!selected || !candidate) return null;
    const fields: Array<{ label: string; key: keyof Pick<PosTower, 'cpu' | 'powerGrid' | 'shieldRadius' | 'maxStructureDistance' | 'shieldHP' | 'armorHP' | 'structureHP'> }> = [
      { label: 'CPU output', key: 'cpu' }, { label: 'Powergrid output', key: 'powerGrid' },
      { label: 'Shield radius', key: 'shieldRadius' }, { label: 'Max structure distance', key: 'maxStructureDistance' },
      { label: 'Shield HP', key: 'shieldHP' }, { label: 'Armor HP', key: 'armorHP' }, { label: 'Structure HP', key: 'structureHP' },
    ];
    return fields.map(({ label, key }) => {
      const current = selected[key] ?? null;
      const compared = candidate[key] ?? null;
      return { label, current, compared, delta: current !== null && compared !== null ? compared - current : null };
    });
  });

  function saveDraft(): void {
    if (readOnly.value) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(planDocument(plan.value))); }
    catch { /* Storage may be disabled; the plan remains usable for this session. */ }
  }

  function restoreDraft(): PosPlanSnapshot | null {
    try { return normalizeSnapshot(JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null')); }
    catch { return null; }
  }

  function queueCalculation(): void {
    window.clearTimeout(calculationTimer);
    calculationTimer = window.setTimeout(() => void refreshCalculation(), 250);
  }

  async function refreshCalculation(): Promise<void> {
    const sequence = ++calculationSequence;
    if (!plan.value.towerTypeID) {
      calculation.value = null;
      calculatedResourceFingerprint.value = null;
      error.value = null;
      return;
    }
    const fingerprint = resourceFingerprint(plan.value);
    try {
      const next = await calculatePos(planDocument(plan.value));
      if (sequence !== calculationSequence) return;
      calculation.value = next;
      calculatedResourceFingerprint.value = fingerprint;
      error.value = null;
    } catch (cause) {
      if (sequence !== calculationSequence) return;
      error.value = cause instanceof Error ? cause.message : 'Unable to calculate this POS plan.';
    }
  }

  function hydrateTower(): void {
    if (!plan.value.towerTypeID) { plan.value.tower = null; return; }
    const matchingTower = towers.value.find(tower => tower.typeID === plan.value.towerTypeID);
    if (matchingTower) plan.value.tower = matchingTower;
  }

  async function loadCatalog(): Promise<void> {
    catalogLoading.value = true;
    error.value = null;
    try {
      const [nextTowers, nextCatalog] = await Promise.all([
        fetchPosTowers(), fetchPosStructures(catalogFilters.value),
      ]);
      towers.value = nextTowers;
      catalog.value = nextCatalog;
      hydrateTower();
      if (comparisonTower.value) comparisonTower.value = towers.value.find(tower => tower.typeID === comparisonTower.value?.typeID) ?? null;
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : 'Unable to load POS catalog.';
    } finally { catalogLoading.value = false; }
  }

  async function loadPlans(): Promise<void> {
    try { ownedPlans.value = await listPosPlans(); }
    catch (cause) { error.value = cause instanceof Error ? cause.message : 'Unable to list saved POS plans.'; }
  }

  async function searchCatalog(): Promise<void> {
    catalogLoading.value = true;
    try { catalog.value = await fetchPosStructures(catalogFilters.value); }
    catch (cause) { error.value = cause instanceof Error ? cause.message : 'Unable to search POS catalog.'; }
    finally { catalogLoading.value = false; }
  }

  function beginLocalDraft(reset = false): void {
    plan.value = reset ? newDraft() : restoreDraft() ?? newDraft();
    readOnly.value = false;
    loadedShareToken.value = null;
    selectedIds.value = [];
    hydrateTower();
    void refreshCalculation();
  }

  function selectTower(tower: PosTower): void {
    if (readOnly.value) return;
    plan.value.towerTypeID = tower.typeID;
    plan.value.tower = tower;
    queueCalculation();
  }

  function setComparisonTower(tower: PosTower | null): void {
    comparisonTower.value = tower;
  }

  function addStructure(item: PosCatalogStructure, quantity = 1): number {
    if (readOnly.value) return 0;
    const count = boundedInteger(quantity, 1, 50, 1);
    const amount = Math.min(count, POS_MAX_STRUCTURES - plan.value.structures.length);
    const added: string[] = [];
    for (let offset = 0; offset < amount; offset++) {
      const index = plan.value.structures.length;
      const position = radialPlacement(index, item.placement?.minDistance);
      const structure: PosStructure = {
        id: localId('structure'), typeID: item.typeID, typeName: item.typeName, role: item.role,
        state: 'offline', x: position.x, y: position.y, z: 0, sortOrder: index,
        lifecycle: item.lifecycle, effectConfidence: item.effectConfidence, footprint: item.footprint,
        cpu: item.cpu, powerGrid: item.powerGrid, primaryEffect: item.primaryEffect, placement: item.placement,
      };
      plan.value.structures.push(structure);
      added.push(structure.id);
    }
    if (added.length) selectedIds.value = added;
    queueCalculation();
    return added.length;
  }

  function selectStructures(ids: string[], append = false): void {
    const validIds = ids.filter(id => plan.value.structures.some(structure => structure.id === id));
    selectedIds.value = append ? [...new Set([...selectedIds.value, ...validIds])] : validIds;
  }

  function updateStructure(id: string, patch: Partial<Pick<PosStructure, 'x' | 'y' | 'z' | 'state' | 'label' | 'loadedQuantity' | 'chargeTypeID' | 'deploymentPhase'>>): void {
    if (readOnly.value) return;
    const structure = plan.value.structures.find(candidate => candidate.id === id);
    if (!structure) return;
    if (patch.x !== undefined) structure.x = clampCoordinate(patch.x);
    if (patch.y !== undefined) structure.y = clampCoordinate(patch.y);
    if (patch.z !== undefined) structure.z = clampCoordinate(patch.z);
    if (patch.state !== undefined) structure.state = patch.state;
    if (patch.label !== undefined) structure.label = patch.label.slice(0, 160);
    if (patch.loadedQuantity !== undefined) structure.loadedQuantity = boundedInteger(patch.loadedQuantity, 0, 1_000_000, 0);
    if (patch.chargeTypeID !== undefined) structure.chargeTypeID = asPositiveInteger(patch.chargeTypeID) ?? null;
    if (patch.deploymentPhase !== undefined) structure.deploymentPhase = patch.deploymentPhase.slice(0, 80);
    queueCalculation();
  }

  function setSelectedState(state: PosStructureState): void {
    if (readOnly.value) return;
    for (const structure of selectedStructures.value) structure.state = state;
    queueCalculation();
  }

  function removeSelected(): void {
    if (readOnly.value || !selectedIds.value.length) return;
    plan.value.structures = plan.value.structures.filter(structure => !selectedIds.value.includes(structure.id));
    plan.value.structures.forEach((structure, index) => { structure.sortOrder = index; });
    selectedIds.value = [];
    queueCalculation();
  }

  function duplicateSelected(): void {
    if (readOnly.value) return;
    const available = POS_MAX_STRUCTURES - plan.value.structures.length;
    const copies = selectedStructures.value.slice(0, available).map((structure, index) => ({
      ...structure, id: localId('structure'), x: clampCoordinate(structure.x + 500), y: clampCoordinate(structure.y + 500),
      sortOrder: plan.value.structures.length + index,
    }));
    plan.value.structures.push(...copies);
    selectedIds.value = copies.map(structure => structure.id);
    queueCalculation();
  }

  function autoArrange(): void {
    if (readOnly.value) return;
    const targets = selectedStructures.value.length ? selectedStructures.value : plan.value.structures;
    targets.forEach((structure, index) => {
      const position = radialPlacement(index, structure.placement?.minDistance, 12);
      structure.x = position.x;
      structure.y = position.y;
    });
    queueCalculation();
  }

  async function save(): Promise<void> {
    if (readOnly.value) throw new Error('Shared plans are read-only. Duplicate the plan before editing.');
    loading.value = true;
    try {
      const document = planDocument(plan.value);
      let saved: PosServerPlan;
      if (plan.value.id == null) {
        saved = await createPosPlan(document);
      } else {
        if (typeof plan.value.revision !== 'number') throw new Error('This saved plan has no revision. Reload it before saving.');
        saved = await updatePosPlan(plan.value.id, document);
      }
      plan.value = saved;
      calculation.value = saved.calculation ?? calculation.value;
      hydrateTower();
      lastSavedAt.value = new Date().toISOString();
      saveDraft();
      await loadPlans();
    } finally { loading.value = false; }
  }

  async function loadOwned(id: number): Promise<void> {
    loading.value = true;
    try {
      const loaded = await readPosPlan(id);
      plan.value = loaded;
      calculation.value = loaded.calculation ?? null;
      readOnly.value = false;
      loadedShareToken.value = null;
      selectedIds.value = [];
      hydrateTower();
      await refreshCalculation();
    } finally { loading.value = false; }
  }

  async function deleteCurrent(): Promise<void> {
    if (readOnly.value || plan.value.id == null) return;
    loading.value = true;
    try {
      await deletePosPlan(plan.value.id);
      beginLocalDraft(true);
      await loadPlans();
    } finally { loading.value = false; }
  }

  async function loadShare(shareId: string): Promise<void> {
    loading.value = true;
    try {
      const loaded = await readPosShare(shareId);
      plan.value = loaded;
      calculation.value = loaded.calculation ?? null;
      readOnly.value = true;
      loadedShareToken.value = shareId;
      selectedIds.value = [];
      hydrateTower();
      await refreshCalculation();
    } finally { loading.value = false; }
  }

  async function duplicate(): Promise<void> {
    if (plan.value.id == null) {
      const copy = normalizeSnapshot(planDocument(plan.value));
      if (!copy) throw new Error('Unable to duplicate this local draft.');
      copy.id = undefined; copy.revision = undefined; copy.shareId = null; copy.name = `${copy.name} copy`.slice(0, 160);
      plan.value = copy; readOnly.value = false; saveDraft();
      return;
    }
    const duplicate = loadedShareToken.value
      ? await duplicatePosShare(loadedShareToken.value)
      : await duplicatePosPlan(plan.value.id);
    plan.value = duplicate;
    readOnly.value = false;
    loadedShareToken.value = null;
    selectedIds.value = [];
    hydrateTower();
    saveDraft();
    await refreshCalculation();
  }

  async function share(): Promise<string> {
    if (plan.value.id == null) throw new Error('Save the plan before creating a share link.');
    if (typeof plan.value.revision !== 'number') throw new Error('Reload this plan before sharing.');
    const result = await createPosShare(plan.value.id, plan.value.revision);
    plan.value = result.plan;
    hydrateTower();
    return result.url ?? `${window.location.origin}/pos/${result.shareId}`;
  }

  async function revokeShare(): Promise<void> {
    if (plan.value.id == null) return;
    if (typeof plan.value.revision !== 'number') throw new Error('Reload this plan before revoking its share.');
    plan.value = await revokePosShare(plan.value.id, plan.value.revision);
    hydrateTower();
  }

  async function importServerDocument(document: unknown): Promise<void> {
    const validation = await validatePosImport(document);
    if (!validation.valid) {
      const details = validation.errors ?? validation.issues?.map(issue => `${issue.path}: ${issue.message}`);
      throw new Error(details?.join(' ') || 'The POS import is invalid.');
    }
    plan.value = await importPosDocument(document);
    readOnly.value = false;
    loadedShareToken.value = null;
    selectedIds.value = [];
    hydrateTower();
    saveDraft();
    await refreshCalculation();
  }


  function addInventoryLine(line: Omit<PosInventoryLine, 'id'>): void {
    if (readOnly.value) return;
    const typeID = asPositiveInteger(line.typeID);
    const quantity = asPositiveInteger(line.quantity);
    if (!typeID || !quantity || !INVENTORY_KINDS.has(line.kind)) throw new Error('Inventory requires a valid type ID, quantity, and kind.');
    const existing = (plan.value.inventory ?? []).find(item => item.typeID === typeID && item.kind === line.kind);
    if (existing) existing.quantity = Math.min(100_000_000, existing.quantity + quantity);
    else (plan.value.inventory ??= []).push({ typeID, quantity: Math.min(quantity, 100_000_000), kind: line.kind, label: line.label?.slice(0, 160) });
    queueCalculation();
  }

  function removeInventoryLine(typeID: number, kind: PosInventoryKind): void {
    if (readOnly.value) return;
    plan.value.inventory = (plan.value.inventory ?? []).filter(item => item.typeID !== typeID || item.kind !== kind);
    queueCalculation();
  }

  function importPlan(text: string): void {
    const payload = JSON.parse(text) as { format?: string; version?: number; plan?: unknown };
    if (payload.format !== 'evefit-pos' || payload.version !== 1) throw new Error('This is not a supported EveFit POS export.');
    const normalized = normalizeSnapshot(payload.plan);
    if (!normalized) throw new Error('The POS export has no valid plan.');
    normalized.id = undefined;
    normalized.revision = undefined;
    normalized.shareId = null;
    plan.value = normalized;
    readOnly.value = false;
    selectedIds.value = [];
    hydrateTower();
    saveDraft();
    queueCalculation();
  }

  function exportPlan(): string {
    return JSON.stringify({ format: 'evefit-pos', version: 1, exportedAt: new Date().toISOString(), plan: planDocument(plan.value) }, null, 2);
  }

  watch(() => draftFingerprint(plan.value), () => {
    saveDraft();
    queueCalculation();
  });

  return {
    plan, towers, catalog, calculation, selectedIds, selectedStructure, selectedStructures, comparisonTower, towerComparison, ownedPlans,
    readOnly, loading, catalogLoading, error, lastSavedAt, catalogFilters, isSaved, shareUrl, resourceHeadroom,
    beginLocalDraft, loadCatalog, loadPlans, searchCatalog, selectTower, setComparisonTower, addStructure, selectStructures, updateStructure,
    setSelectedState, removeSelected, duplicateSelected, autoArrange, addInventoryLine, removeInventoryLine, refreshCalculation,
    save, loadOwned, deleteCurrent, loadShare, duplicate, share, revokeShare, importPlan, importServerDocument, exportPlan,
  };
});
